import type { BluffMove, BluffView } from "@bing/engine-bluff";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { BluffTable } from "../screens/BluffTable";
import { useSession } from "../store/context";
import type { SeatPlayer } from "./seat-identity";
import type { LobbyPlayer } from "./online-connection";
import { WinOverlay } from "./BluffGame";
import { OnlineGame } from "./OnlineGame";

/** The lobby roster as the felt and the result card both want it. ONE mapping, so a chair can
 *  never be a named person on the table and a nameless stand-in on the end screen. */
function seatPlayers(
  players: readonly LobbyPlayer[] | undefined,
): readonly SeatPlayer[] | undefined {
  return players?.map((p) => ({
    seat: p.seat,
    isBot: p.isBot,
    // The server resolves the display name; feeding it through `nickname` is what makes a seat
    // read app-consistently via `displayName()`.
    nickname: p.name,
    avatarId: p.user?.avatarId,
    avatarPhotoId: p.user?.avatarPhotoId,
    isOfficial: p.user?.isOfficial,
  }));
}

/** Online بلوف — thin wrapper over the shared `OnlineGame` shell. */
export function BluffOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  return (
    <OnlineGame<BluffMove, BluffView>
      gameId="bluff"
      room="bluff-online"
      title={fa.bluff.title}
      renderTable={({ view, legal, send, leave, players, seatNotices }) => (
        <BluffTable
          seatNotices={seatNotices}
          view={view}
          legal={legal}
          onMove={send}
          onLeave={leave}
          self={profile}
          players={seatPlayers(players)}
        />
      )}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) => (
        <WinOverlay
          result={result}
          you={view.you}
          // The SAME identity pair the felt gets — without them the result card called every
          // real opponent «بازیکن N» and gave them a stand-in face.
          self={profile}
          players={seatPlayers(players)}
          onAgain={again}
          againDisabled={againDisabled}
          againHint={againHint}
          againHidden={againHidden}
          onLeave={leave}
        />
      )}
    />
  );
}
